/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { Commands } from 'bridge-commands/commands';
import { Types as CommandTypes } from 'bridge-commands/types';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginHandlers, PluginResponse } from 'crimson/lib/bridge/common/plugin.models';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';
import { NativeEvents } from 'crimson/lib/native-events/native-events';
import { BrowserLeanModeService } from 'crimson/lib/browser-lean-mode-service/browser-lean-mode.service';
import { generateUuid } from 'utils/lib/common/common-utils';
import { IpcEvents } from '../../../../../projects/gfn/shared/src/lib/ipc/ipc';
import { PluginMessages } from '../shared';

import Types = CommandTypes.Common.Ipc;

interface IpcMessageEvent extends MessageEvent {
    data: Types.IpcMessage;
}

interface LocalStorageBroadcastPayload {
    message: Types.IpcMessage;
    senderUuid: string;
}

const BroadcastChannelName = 'WebclientChannel';
const Ipc = Commands.Common.Ipc;

const LeanModeAllowedMessageTypes = [IpcEvents.PushNotification];

@Injectable({
    providedIn: 'root'
})
export class IpcPlugin extends BrowserPlugin implements OnDestroy {
    private broadcastChannel: BroadcastChannel;
    private localStorage: Storage;
    private storageEventListener: (event: StorageEvent) => void;
    private uuid: string;
    private messages: Array<string>;
    private leanModeDisabled = true;

    constructor(browserLeanModeService: BrowserLeanModeService) {
        super();
        this.startBroadcastListener();
        browserLeanModeService.leanModeTransitionState.subscribe(leanMode => {
            this.leanModeDisabled = !leanMode;
        });
    }

    ngOnDestroy() {
        this.stopBroadcastListener();
    }

    private shouldHandleIPC(messageType: IpcEvents): boolean {
        // always handle IPC is lean mode is disabled
        if (this.leanModeDisabled) {
            return true;
        }

        // handle only allowed message types when lean mode is enabled
        return LeanModeAllowedMessageTypes.includes(messageType);
    }

    private startBroadcastListener() {
        this.messages = [];
        /* istanbul ignore else */
        if (typeof BroadcastChannel !== 'undefined') {
            this.broadcastChannel = new BroadcastChannel(BroadcastChannelName);
            this.broadcastChannel.onmessage = (event: IpcMessageEvent) => {
                if (this.shouldHandleIPC(event?.data?.type as IpcEvents)) {
                    this.onReceiveMessage(event.data);
                }
            };
        } else if (this.localStorageAvailable()) {
            this.localStorage = localStorage;
            this.uuid = generateUuid();
            this.storageEventListener = (event: StorageEvent) => {
                if (event.key !== BroadcastChannelName) {
                    return;
                }
                const payload = JSON.parse(event.newValue) as LocalStorageBroadcastPayload;
                if (
                    !payload ||
                    // Ignore broadcast sent by self.
                    payload.senderUuid === this.uuid
                ) {
                    return;
                }
                if (this.shouldHandleIPC(payload.message?.type as IpcEvents)) {
                    this.onReceiveMessage(payload.message);
                }
            };
            window.addEventListener('storage', this.storageEventListener);
        }
    }

    private stopBroadcastListener() {
        if (this.localStorage && this.storageEventListener) {
            window.removeEventListener('storage', this.storageEventListener);
        }
        if (this.broadcastChannel) {
            this.broadcastChannel.close();
            this.broadcastChannel = null;
        }
    }

    public get handlers(): PluginHandlers {
        return {
            module: Ipc.Pop.module,
            commands: {
                [Ipc.Pop.method]: this.popMessage.bind(this),
                [Ipc.Push.method]: this.pushMessage.bind(this)
            }
        };
    }

    private pushMessage(command: FullBridgeCommand<Types.PushParameters>): Observable<PluginResponse> {
        if (this.shouldHandleIPC(command.payload.message?.type as IpcEvents)) {
            this.broadcastMessage(command.payload.message);
        }
        return this.makeResult('IPC Push Message successful');
    }

    private popMessage(command: FullBridgeCommand<any>): Observable<PluginResponse> {
        /* istanbul ignore if */
        // Messages are not received by same browser tab, ignoring in coverage
        // As we are selective about messages getting added to queue, allow popping of any messages
        if (this.messages.length) {
            const val = this.messages.pop();
            return this.makeResult(val);
        } else {
            const emptyQueue: Types.IpcMessage = {
                type: IpcEvents.QueueEmpty,
                content: JSON.stringify(false)
            };
            return this.makeResult(JSON.stringify(emptyQueue));
        }
    }

    private broadcastMessage(message: Types.IpcMessage) {
        /* istanbul ignore else */
        if (this.broadcastChannel) {
            this.broadcastChannel.postMessage(message);
        } else if (this.localStorage) {
            // Set an item to fire storage event.
            this.localStorage.setItem(BroadcastChannelName, JSON.stringify({ message, senderUuid: this.uuid }));
            // Remove the item immediately, which fires another storage event that we will ignore.
            this.localStorage.removeItem(BroadcastChannelName);
        }
    }

    private onReceiveMessage(message: Types.IpcMessage) {
        this.messages.push(JSON.stringify(message));

        // notify other plugins that a new IPC message has arrived
        PluginMessages.next({
            sender: 'IpcPlugin',
            data: NativeEvents.IpcNewMessage
        });
    }

    private localStorageAvailable(): boolean {
        try {
            const test = '__storage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }
}
